// apps/web/src/features/commercial-proposals/components/CommercialProposalEditorBody.tsx
//
// @aiPurpose the editor form shell shared by the create host (`/kp/new`) and
//   the draft edit mode inside the detail drawer (#1198): header, scrollable
//   grouped fields + live calc preview, and a footer with cancel/submit. The
//   live preview is recomputed from the form on every keystroke; it is
//   replaced by the backend `calculation` once the save round-trips.
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, useEditorGuard } from '../../../ui/index.js';
import type { AppLocale } from '../../../i18n/index.js';
import type { CommercialProposalFormState } from '../formModel.js';
import type { CommercialProposalLiveCalc } from '../calc.js';
import { CommercialProposalEditorFields } from './CommercialProposalEditorFields.js';
import { CommercialProposalCalcPanel } from './CommercialProposalCalcPanel.js';

type SetField = <K extends keyof CommercialProposalFormState>(
  key: K,
  value: CommercialProposalFormState[K],
) => void;

export function CommercialProposalEditorBody({
  title,
  form,
  setField,
  preview,
  valid,
  language,
  submitting,
  submitLabel,
  onClose,
  onSubmit,
  banner,
  proposalId = null,
  savedPhotoObjectKey = '',
}: {
  title: string;
  form: CommercialProposalFormState;
  setField: SetField;
  preview: CommercialProposalLiveCalc | null;
  valid: boolean;
  language: AppLocale;
  submitting: boolean;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  banner?: ReactNode;
  /** Proposal id when editing a saved draft; null in the create host. */
  proposalId?: string | null;
  /** Photo key the proposal was loaded with (saved-vs-local preview). */
  savedPhotoObjectKey?: string;
}) {
  const { t } = useTranslation('commercialProposals');
  const { confirmDestructive } = useEditorGuard();
  const requestClose = () => confirmDestructive(onClose);

  // WHY: block submit while a photo upload is in flight — the submitter
  // serializes the form immediately, so submitting mid-upload would drop the
  // just-selected (not-yet-bound) photo. The control reports this flag. The
  // setState identity is stable, so the control's effect dep stays stable.
  const [photoUploading, setPhotoUploading] = useState(false);

  // WHY: submit is gated on the shared content schema's validity, which
  // subsumes the old productName-only check (it enforces `productName.min(1)`
  // plus the strictly-positive rates/divisors) — so silently-coerced values
  // can never be saved.
  const canSubmit = !submitting && valid && !photoUploading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="commercial-proposals-editor"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-1)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</h2>
        <IconButton
          icon="x"
          label={t('editor.cancel')}
          onClick={requestClose}
          data-testid="commercial-proposals-editor-close"
        />
      </div>
      <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', minHeight: 0 }}>
        {banner}
        <CommercialProposalEditorFields
          form={form}
          setField={setField}
          proposalId={proposalId}
          savedPhotoObjectKey={savedPhotoObjectKey}
          onPhotoUploadingChange={setPhotoUploading}
        />
        {preview ? (
          <CommercialProposalCalcPanel preview={preview} language={language} />
        ) : (
          <div
            data-testid="commercial-proposals-calc-unavailable"
            style={{ marginTop: 18, fontSize: 11, color: 'var(--fg-3)' }}
          >
            {t('calc.unavailable')}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: '12px 20px',
          borderTop: '1px solid var(--border-1)',
        }}
      >
        <Button kind="secondary" type="button" onClick={requestClose}>
          {t('editor.cancel')}
        </Button>
        <Button
          kind="primary"
          type="submit"
          disabled={!canSubmit}
          data-testid="commercial-proposals-editor-submit"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
